import { localStorageKey } from "@/enums/localStorageKey";
import { accessTokenRefresh } from "@/modules/authenticationService";

import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "@/utils/localStorageHelper";
import { navigateToLogin } from "@/utils/navigationHelper";
import axios from "axios";

const axiosApi = axios.create({
  baseURL: import.meta.env.VITE_LOG_APP_URL,
  timeout: 30000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },

  withCredentials: true,
});
axiosApi.interceptors.response.use(undefined, (error) => {
  return Promise.reject(error);
});
axiosApi.interceptors.request.use(
  (config) => {
    const token = getLocalStorageItem(localStorageKey.ACCESS_TOKEN);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip if already retried or not a 401 error
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip for auth endpoints to prevent infinite loops
    if (originalRequest.url.includes("/auth/")) {
      return Promise.reject(error);
    }

    const refreshToken = getLocalStorageItem(localStorageKey.REFRESH_TOKEN);
    if (!refreshToken) {
      navigateToLogin();
      return Promise.reject(error);
    }

    try {
      // Mark request as retried
      originalRequest._retry = true;
      const refreshToken = getLocalStorageItem(
        localStorageKey.REFRESH_TOKEN,
      ) as string;
      const refreshResponse = await accessTokenRefresh(refreshToken);

      const newAccessToken = refreshResponse.access;

      if (!newAccessToken) {
        throw new Error("No access token in refresh response");
      }

      // Store new token
      setLocalStorageItem(localStorageKey.ACCESS_TOKEN, newAccessToken);

      // Update authorization header
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      // Retry original request with new token
      return axiosApi(originalRequest);
    } catch (refreshError) {
      localStorage.clear();
      navigateToLogin();
      return Promise.reject(refreshError);
    }
  },
);
export default axiosApi;
